/**
 * Fills `{token}` placeholders in a dictionary string.
 *
 * Translated copy cannot interpolate, so any value that varies at runtime — a
 * currency code, a date, a count — has to reach the translator as a placeholder
 * and be substituted here. Every occurrence of each token is replaced; a token
 * the template does not mention is simply ignored, so a translator dropping one
 * degrades the sentence rather than breaking the render, and an unknown token is
 * left visible rather than blanked.
 *
 * Substitution is a SINGLE pass: replacing token by token would let a value that
 * itself contains `{…}` be expanded again by a later token.
 */
export function fillTemplate(
  template: string,
  tokens: Record<string, string>
): string {
  return template.replace(/\{(\w+)\}/g, (match, token: string) =>
    Object.prototype.hasOwnProperty.call(tokens, token) ? tokens[token]! : match
  );
}

/**
 * `value` with its first letter upper-cased and the rest left as written — for
 * names an editor may have typed in lower case ("artemis" → "Artemis") without
 * flattening the ones that are not ("WeHunt Collar", "G500FI").
 */
export function capitalizeFirst(value: string): string {
  return value.charAt(0).toLocaleUpperCase() + value.slice(1);
}

/**
 * Split a description on the first paragraph boundary.
 *
 * Designed for Umbraco rich-text "description" output, which is a flat
 * sequence of sibling block elements (`<p>...</p><p>...</p>`) or plain
 * text separated by `<br>`/newlines. The split is performed via regex,
 * which assumes paragraphs are NOT nested and do not contain other
 * paragraphs as descendants. Arbitrary HTML (tables, lists with embedded
 * paragraphs, cross-paragraph inline elements) is out of scope — pass
 * such content through a DOM-aware splitter if needed.
 *
 * Returns `{ first, rest }`. `rest` is `null` when there is no further
 * content after the split point.
 */
export function splitOnFirstBreak(input: string): {
  first: string;
  rest: string | null;
} {
  if (!input) return { first: "", rest: null };

  const paragraphBoundary = /(<\/p>)\s*(<p[^>]*>)/i;
  const paragraphMatch = paragraphBoundary.exec(input);
  if (paragraphMatch) {
    const idx = paragraphMatch.index;
    const closingTag = paragraphMatch[1] ?? "";
    const openingTag = paragraphMatch[2] ?? "";
    const first = input.slice(0, idx) + closingTag;
    const tail = openingTag + input.slice(idx + paragraphMatch[0].length);
    // Normalize: drop a lone trailing empty <p></p> and trim whitespace.
    const tailNormalized = tail.replace(/^<p[^>]*>\s*<\/p>$/i, "").trim();
    if (!tailNormalized) {
      return { first, rest: null };
    }
    return { first, rest: tailNormalized };
  }

  const inlinePatterns: RegExp[] = [/<br\s*\/?>/i, /\r?\n/];
  for (const re of inlinePatterns) {
    const match = re.exec(input);
    if (match) {
      const idx = match.index;
      const splitEnd = idx + match[0].length;
      const first = input.slice(0, idx);
      const rest = input.slice(splitEnd).trim();
      if (!rest) {
        return { first, rest: null };
      }
      return { first, rest };
    }
  }
  return { first: input, rest: null };
}
